import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Navbar from 'components/Header/Navbar';
import Button from 'components/Button';
import Modal from 'components/Modal/Modal';
import { useAuth } from 'contexts/AuthContext';
import { useReelsFeed } from 'hooks/useReelsFeed';
import { useCreateReel } from 'hooks/useCreateReel';
import { useDeleteReel } from 'hooks/useDeleteReel';
import ReelPlayer from 'components/reels/ReelPlayer';
import ReelUpload from 'components/reels/ReelUpload';
import type { ReelViewModel } from 'api/types/reel';

const SLIDE_DURATION_MS = 220;

const Gonderiler: React.FC = () => {
  const { user } = useAuth();
  const {
    reels,
    loading,
    loadingMore,
    error: feedError,
    hasMore,
    loadMore,
    refetch,
    prependCreatedReel,
    addSeenId,
  } = useReelsFeed();
  const { createReel, loading: uploadLoading, error: uploadError, resetError: resetUploadError } = useCreateReel();
  const { deleteReel, loading: deleteLoading, error: deleteError } = useDeleteReel();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'up' | 'down' | null>(null);
  const [isSliding, setIsSliding] = useState(false);
  const [infoModal, setInfoModal] = useState<{ title: string; message: string } | null>(null);
  const slideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleUpload = useCallback(
    async (file: File) => {
      resetUploadError();
      const result = await createReel(file);
      if (!result.isSuccess) return;

      if (result.createdReel) {
        prependCreatedReel(result.createdReel);
        setCurrentIndex(0);
        window.scrollTo({ top: 0, behavior: 'auto' });
        void refetch(result.createdReel);
        return;
      }

      // Successful create without payload: rely on feed refresh.
      setCurrentIndex(0);
      window.scrollTo({ top: 0, behavior: 'auto' });
      await refetch();
    },
    [createReel, prependCreatedReel, refetch, resetUploadError]
  );

  const handleDelete = useCallback(
    async (reel: ReelViewModel) => {
      if (!user?.id) {
        setInfoModal({
          title: 'Giriş Gerekli',
          message: 'Reel silmek için giriş yapmalısınız.',
        });
        return;
      }
      if (String(user.id) !== String(reel.userId)) {
        setInfoModal({
          title: 'Yetkisiz İşlem',
          message: 'Bu reeli yalnızca sahibi silebilir.',
        });
        return;
      }

      const result = await deleteReel(reel.id);
      if (result.ok) {
        await refetch();
      }
      setInfoModal({
        title: result.ok ? 'Reel Silindi' : 'Silme Başarısız',
        message: result.message,
      });
    },
    [user?.id, deleteReel, refetch]
  );

  const goNext = useCallback(() => {
    if (isSliding || reels.length === 0) return;
    if (currentIndex >= reels.length - 1) {
      if (hasMore && !loadingMore) loadMore();
      return;
    }
    setIsSliding(true);
    setSlideDirection('up');
    slideTimeoutRef.current = setTimeout(() => {
      setCurrentIndex((i) => i + 1);
      setSlideDirection(null);
      setIsSliding(false);
      slideTimeoutRef.current = null;
    }, SLIDE_DURATION_MS);
  }, [currentIndex, reels.length, hasMore, loadingMore, loadMore, isSliding]);

  const goPrev = useCallback(() => {
    if (isSliding || currentIndex <= 0) return;
    setIsSliding(true);
    setSlideDirection('down');
    slideTimeoutRef.current = setTimeout(() => {
      setCurrentIndex((i) => i - 1);
      setSlideDirection(null);
      setIsSliding(false);
      slideTimeoutRef.current = null;
    }, SLIDE_DURATION_MS);
  }, [currentIndex, isSliding]);

  useEffect(() => {
    return () => {
      if (slideTimeoutRef.current) clearTimeout(slideTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (!hasMore || loadingMore || reels.length === 0) return;
    if (currentIndex >= reels.length - 2) loadMore();
  }, [currentIndex, reels.length, hasMore, loadingMore, loadMore]);

  useEffect(() => {
    if (reels.length === 0) {
      setCurrentIndex(0);
      return;
    }
    setCurrentIndex((prev) => Math.min(prev, reels.length - 1));
  }, [reels.length]);

  const currentReel = reels[currentIndex] ?? null;
  const nextReel = slideDirection === 'up' && currentIndex + 1 < reels.length ? reels[currentIndex + 1] : null;
  const prevReel = slideDirection === 'down' && currentIndex > 0 ? reels[currentIndex - 1] : null;
  const hasRenderableCurrentReel = !!currentReel?.videoUrl;

  useEffect(() => {
    console.log('[Gonderiler] Render state', {
      loading,
      loadingMore,
      feedError,
      reelsLength: reels.length,
      currentIndex,
      currentReelId: currentReel?.id ?? null,
      currentReelVideoUrl: currentReel?.videoUrl ?? null,
      hasRenderableCurrentReel,
      hasMore,
      scrollY: window.scrollY,
    });
  }, [
    loading,
    loadingMore,
    feedError,
    reels.length,
    currentIndex,
    currentReel?.id,
    currentReel?.videoUrl,
    hasRenderableCurrentReel,
    hasMore,
  ]);

  return (
    <div className="gonderiler-page">
      <Navbar />

      <div className="reels-layout">
        <div className="reels-column">
          <div className="reels-header">
            <h1 className="reels-title">Gönderiler</h1>
            <ReelUpload
              onSelect={handleUpload}
              loading={uploadLoading}
              error={uploadError}
              disabled={!user}
            />
          </div>

          {feedError && (
            <div className="reels-error alert alert-danger mx-3 mt-3" role="alert">
              {feedError}
              <Button className="btn btn-sm btn-outline-danger ms-2" onClick={() => refetch()}>
                Tekrar dene
              </Button>
            </div>
          )}

          {deleteError && (
            <div className="alert alert-danger mx-3 mt-2" role="alert">
              {deleteError}
            </div>
          )}

          {loading && (
            <div className="reels-skeleton-fill">
              <div className="reel-skeleton" />
            </div>
          )}

          {!loading && reels.length === 0 && !feedError && (
            <div className="reels-empty">
              <i className="fas fa-video fa-3x text-secondary mb-3" />
              <p className="text-white-50">Henüz reel yok.</p>
              {user && (
                <ReelUpload
                  onSelect={handleUpload}
                  loading={uploadLoading}
                  error={uploadError}
                />
              )}
            </div>
          )}

          {!loading && reels.length > 0 && (
            <div className="reels-viewport">
              <div className="reels-viewport-inner">
                {slideDirection === 'up' && nextReel && (
                  <div className="reel-slide reel-slide-enter-up" key={`next-${nextReel.id}`}>
                    <ReelPlayer videoUrl={nextReel.videoUrl} isActive onVisible={() => addSeenId(nextReel.id)} />
                    <div className="reel-overlay">
                      {user?.id === nextReel.userId && (
                        <button
                          type="button"
                          className="reel-delete-btn btn btn-danger btn-sm"
                          onClick={() => handleDelete(nextReel)}
                          disabled={deleteLoading}
                          title="Reeli sil"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {slideDirection === 'down' && prevReel && (
                  <div className="reel-slide reel-slide-enter-down" key={`prev-${prevReel.id}`}>
                    <ReelPlayer videoUrl={prevReel.videoUrl} isActive onVisible={() => addSeenId(prevReel.id)} />
                    <div className="reel-overlay">
                      {user?.id === prevReel.userId && (
                        <button
                          type="button"
                          className="reel-delete-btn btn btn-danger btn-sm"
                          onClick={() => handleDelete(prevReel)}
                          disabled={deleteLoading}
                          title="Reeli sil"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {hasRenderableCurrentReel && currentReel && (
                  <div
                    className={`reel-slide reel-slide-current${slideDirection ? ` reel-slide-leave-${slideDirection}` : ''}`}
                    key={currentReel.id}
                  >
                    <ReelPlayer
                      videoUrl={currentReel.videoUrl}
                      isActive={!isSliding}
                      onVisible={() => addSeenId(currentReel.id)}
                    />
                    <div className="reel-overlay">
                      {user?.id === currentReel.userId && (
                        <button
                          type="button"
                          className="reel-delete-btn btn btn-danger btn-sm"
                          onClick={() => handleDelete(currentReel)}
                          disabled={deleteLoading}
                          title="Reeli sil"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {!hasRenderableCurrentReel && (
                  <div className="reels-empty reels-empty-in-viewport">
                    <i className="fas fa-video-slash fa-3x text-secondary mb-3" />
                    <p className="text-white-50">Gösterilecek uygun reel bulunamadı.</p>
                  </div>
                )}
              </div>

              <button
                type="button"
                className="reel-nav-btn reel-nav-prev"
                onClick={goPrev}
                disabled={currentIndex <= 0 || isSliding}
                aria-label="Önceki"
              >
                <i className="fas fa-chevron-up" />
              </button>
              <button
                type="button"
                className="reel-nav-btn reel-nav-next"
                onClick={goNext}
                disabled={isSliding || (currentIndex >= reels.length - 1 && !hasMore)}
                aria-label="Sonraki"
              >
                <i className="fas fa-chevron-down" />
              </button>

              {loadingMore && (
                <div className="reel-loading-more">
                  <span className="spinner-border spinner-border-sm text-light" role="status" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <footer className="footer mt-auto py-5 bg-white border-top">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-3 text-center">
              <h5 className="mb-3 text-dark">Hakkımızda</h5>
              <p className="text-secondary">
                SadeceBilgisayar.com, sıfır veya İkinci el teknolojik ürünleri güvenilir bir şekilde alım veya satım yapabileceğiniz bir platformdur.
              </p>
            </div>
            <div className="col-lg-3 text-center">
              <h5 className="mb-3 text-dark">Önemli Tavsiyeler</h5>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <Link className="text-secondary text-decoration-none d-inline text-nowrap" to="/masaustu_tavsiyeler">
                    Masaüstü alırken dikkat edilmesi gerekenler
                  </Link>
                </li>
                <li className="mb-2">
                  <Link className="text-secondary text-decoration-none d-inline" to="/laptop-tavsiyeler">
                    Laptop alırken dikkat edilmesi gerekenler
                  </Link>
                </li>
                <li className="mb-2">
                  <Link className="text-secondary text-decoration-none d-inline" to="/donanim-tavsiyeler">
                    Donanım alırken dikkat edilmesi gerekenler
                  </Link>
                </li>
              </ul>
            </div>
            <div className="col-lg-3 text-center">
              <h5 className="mb-3 text-dark">İletişim</h5>
              <div className="contact-info">
                <p className="text-secondary mb-2">Görüşleriniz ve şikayetleriniz için</p>
                <p className="text-secondary mb-2">
                  <i className="fas fa-envelope me-2" />
                  info@sadecebilgisayar.com
                </p>
              </div>
            </div>
            <div className="col-lg-3 text-center">
              <h5 className="mb-3 text-dark">Sosyal Medya Hesaplarımız</h5>
              <div className="social-links">
                <a className="text-primary me-4" href="#">
                  <i className="fab fa-facebook fa-3x" />
                </a>
                <a className="text-danger me-4" href="#">
                  <i className="fab fa-instagram fa-3x" />
                </a>
                <a className="text-danger" href="#">
                  <i className="fab fa-youtube fa-3x" />
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom mt-5 pt-4 border-top">
            <div className="row">
              <div className="col-md-6">
                <p className="text-secondary mb-0">© 2024 SadeceBilgisayar.com - Tüm hakları saklıdır.</p>
              </div>
              <div className="col-md-6 text-md-end">
                <Link className="text-secondary me-3" to="/gizlilik-sozlesmesi">Gizlilik Politikası</Link>
                <Link className="text-secondary me-3" to="/kullanici-sozlesmesi">Kullanım Koşulları</Link>
                <Link className="text-secondary" to="/cerez-politikasi">Çerez Politikası</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {infoModal && (
        <Modal
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal(null)}
        />
      )}

      <style>{`
        .gonderiler-page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: #000;
          overflow-x: hidden;
          overflow-y: auto;
        }
        .reels-layout {
          flex: 1;
          flex-shrink: 0;
          min-height: 0;
          height: calc(100vh - 56px);
          display: flex;
          justify-content: center;
          background: rgba(0,0,0,0.95);
        }
        .reels-column {
          width: 100%;
          max-width: 420px;
          height: calc(100vh - 56px);
          min-height: calc(100vh - 56px);
          min-height: 0;
          display: flex;
          flex-direction: column;
          background: #000;
          box-shadow: 0 0 24px rgba(0,0,0,0.5);
        }
        .reels-header {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: rgba(0,0,0,0.9);
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }
        .reels-title {
          margin: 0;
          font-size: 1.25rem;
          color: #fff;
        }
        .reels-viewport {
          flex: 1;
          min-height: 0;
          position: relative;
          overflow: hidden;
          height: calc(100vh - 56px);
          max-height: calc(100dvh - 56px);
          background: #000;
        }
        .reels-viewport-inner {
          position: absolute;
          inset: 0;
        }
        .reel-slide {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          transition: transform ${SLIDE_DURATION_MS}ms ease-out;
        }
        .reel-slide-current {
          z-index: 1;
          transform: translateY(0);
        }
        .reel-slide-current.reel-slide-leave-up {
          transform: translateY(-100%);
        }
        .reel-slide-current.reel-slide-leave-down {
          transform: translateY(100%);
        }
        .reel-slide-enter-up {
          z-index: 2;
          transform: translateY(100%);
          animation: reel-enter-up ${SLIDE_DURATION_MS}ms ease-out forwards;
        }
        .reel-slide-enter-down {
          z-index: 2;
          transform: translateY(-100%);
          animation: reel-enter-down ${SLIDE_DURATION_MS}ms ease-out forwards;
        }
        @keyframes reel-enter-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes reel-enter-down {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
        .reel-slide > div:first-child {
          width: 100%;
          height: 100%;
        }
        .reel-slide video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }
        .reel-overlay {
          position: absolute;
          bottom: 1.5rem;
          right: 1rem;
          pointer-events: auto;
          background: linear-gradient(transparent, rgba(0,0,0,0.5));
          padding: 0.5rem;
          border-radius: 8px;
        }
        .reel-delete-btn { opacity: 0.9; }
        .reel-nav-btn {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: none;
          background: rgba(255,255,255,0.25);
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .reel-nav-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.4);
        }
        .reel-nav-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .reel-nav-prev { top: 1rem; }
        .reel-nav-next { bottom: 1rem; }
        .reel-loading-more {
          position: absolute;
          bottom: 4rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 5;
        }
        .reels-skeleton-fill {
          flex: 1;
          min-height: 0;
          height: calc(100vh - 56px);
          max-height: calc(100dvh - 56px);
        }
        .reel-skeleton {
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
          background-size: 200% 100%;
          animation: reel-skeleton-shine 1.2s ease-in-out infinite;
        }
        @keyframes reel-skeleton-shine {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .reels-empty {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          min-height: 200px;
          background: #000;
        }
        .reels-empty-in-viewport {
          position: absolute;
          inset: 0;
          z-index: 3;
        }
        .reels-error { max-width: 100%; }
      `}</style>
    </div>
  );
};

export default Gonderiler;
